import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, inject } from '@angular/core';
import { NgClass } from "@angular/common";
import { Subject } from "rxjs";
import { NOTIFICATIONS, NotificationsService } from "../../../../core/service/notifications.service";
import { FpModifyValuePipe } from "../../../../util/pipes/fp-modify-value.pipe";
import { BmHeaderShow, BudgetModelSelectorMiniDTO } from "../pojo/bm-classes";
import { BudgetModelFormGroup } from "../pojo/bm-form";
import { SelectedItemStyle, SelectorComponent } from "../../../shared/component/inputs/selector/selector.component";
import { BmButtons, BmInputAction, BmValidationErrors } from "../pojo/bm-enums";
import {
  UnsavedChangesModalService,
} from "../../../shared/component/unsaved-changes-modal-wrapper/service/unsaved-changes-modal.service";
import { BmYearQuarterService } from "../services/general/bm-year-quarter.service";
import { InputComponent } from "../../../shared/component/inputs/input-component/input.component";
import { ReactiveFormsModule } from "@angular/forms";
import { ButtonComponent } from "../../../shared/component/buttons/button.component";
import { ModalDialogComponent } from "../../../shared/component/modal-dialog/modal-dialog.component";

@Component({
  selector: 'app-header-budget-model',
  templateUrl: './header-budget-model.component.html',
  styleUrls: ['./header-budget-model.component.scss'],
  standalone: true,
  imports: [
    FpModifyValuePipe,
    InputComponent,
    ReactiveFormsModule,
    NgClass,
    SelectorComponent,
    ButtonComponent,
    ModalDialogComponent,
  ],
})
export class HeaderBudgetModelComponent implements OnChanges, OnDestroy {
  private unsavedChangesModalService = inject(UnsavedChangesModalService);
  private notificationsService = inject(NotificationsService);

  private _destroyStream: Subject<void> = new Subject<void>();

  @Input({ required: true }) budgetForm: BudgetModelFormGroup;
  @Input({ required: true }) selectorData: BudgetModelSelectorMiniDTO[];
  @Output() buttonClickEmitter: EventEmitter<BmButtons> = new EventEmitter<BmButtons>();
  @Output() modelSelectedEmitter: EventEmitter<string> = new EventEmitter<string>();
  @Output() exportExcelEmitter = new EventEmitter<void>();

  modelSelectedItemStyle = new SelectedItemStyle('Model', 'upper-case black-8');
  selectedItem: BudgetModelSelectorMiniDTO;
  show = new BmHeaderShow();
  deleteModalText: string[];

  protected readonly BmButtons = BmButtons;
  protected readonly BmTimelineAction = BmInputAction;

  ngOnChanges(): void {
    this.chooseSelectorModel();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  /**
   * Select current model in selector
   *  - model exist         => find via UUID
   *  - model doesn't exist => select first
   */
  chooseSelectorModel(): void {
    if (this.budgetForm.getRawValue().uuid) {
      this.selectedItem = this.selectorData.find((model) => model.uuid === this.budgetForm.getRawValue().uuid);
    } else {
      this.selectedItem = this.selectorData[0];
    }
  }

  /**
   * Emit button click actions in the main component. Where needed to be set isEditState
   * @param button type of the clicked button
   */
  buttonClicked(button: BmButtons): void {
    switch (button) {
      case BmButtons.EDIT:
        this.budgetForm.controls.isEditState.patchValue(true);
        break;
      case BmButtons.CANCEL:
        this.budgetForm.controls.isEditState.patchValue(false);
        this.unsavedChangesModalService.setIsInEditMode(false);
        this.buttonClickEmitter.emit(BmButtons.CANCEL);
        break;
      case BmButtons.ADD_NEW_MODEL:
        this.buttonClickEmitter.emit(BmButtons.ADD_NEW_MODEL);
        break;
      case BmButtons.CREATE:
        if (this.budgetForm.invalid) {
          this.showInvalidModelNotification();
          return;
        }
        this.buttonClickEmitter.emit(BmButtons.CREATE);
        this.unsavedChangesModalService.setIsInEditMode(false);
        break;
      case BmButtons.UPDATE:
        if (this.budgetForm.invalid) {
          this.showInvalidModelNotification();
          return;
        }
        this.buttonClickEmitter.emit(BmButtons.UPDATE);
        this.unsavedChangesModalService.setIsInEditMode(false);
        break;
      case BmButtons.DUPLICATE:
        this.buttonClickEmitter.emit(BmButtons.DUPLICATE);
        break;
      case BmButtons.DELETE:
        this.show.modalThinkTwice = true;
        this.deleteModalText = ['Model ' + this.budgetForm.value.name + ' will be deleted'];
        break;
    }
  }

  /**
   * Handle model change in selector
   *
   * @param model selected model in selector
   */
  changeModel(model: BudgetModelSelectorMiniDTO): void {
    this.selectedItem = model;
    this.modelSelectedEmitter.emit(model.uuid);
  }

  /**
   * Handle year numbers change
   *
   * @param action input button type
   */
  changeModelTimeline(action: BmInputAction): void {
    switch (action) {
      case BmInputAction.EDIT:
        this.budgetForm.controls.tempYearsNumber.enable();
        break;
      case BmInputAction.CANCEL:
        this.budgetForm.controls.tempYearsNumber.disable();
        this.budgetForm.controls.tempYearsNumber.patchValue(this.budgetForm.controls.previousYearsNumber.getRawValue(), { emitEvent: false });
        break;
      case BmInputAction.SAVE:
        if (this.budgetForm.controls.tempYearsNumber.hasError('max')) {
          return;
        }
        this.budgetForm.controls.yearsNumber.patchValue(this.budgetForm.controls.tempYearsNumber.getRawValue());
        this.budgetForm.controls.tempYearsNumber.disable();
        BmYearQuarterService.applyYearDelta(this.budgetForm);
        BmYearQuarterService.updateModelHeaders(this.budgetForm.controls.tempYearsNumber.value, this.budgetForm);
        break;
    }
  }

  /**
   * Handle commitment value change
   *
   * @param budgetForm budget modal form
   * @param action input button type
   */
  changeCommitment(budgetForm: BudgetModelFormGroup, action: BmInputAction): void {
    switch (action) {
      case BmInputAction.EDIT:
        budgetForm.controls.totalCommitment.enable();
        break;
      case BmInputAction.CANCEL:
        budgetForm.controls.totalCommitment.disable();
        budgetForm.controls.totalCommitment.patchValue(budgetForm.controls.previousTotalCommitment.value, { emitEvent: false });
        break;
      case BmInputAction.SAVE:
        budgetForm.controls.totalCommitment.disable();
        break;
    }
  }

  /**
   * If delete was confirmed in think twice modal => emit action and close modal
   */
  confirmDeleteModel(confirmation: boolean): void {
    if (confirmation) {
      this.buttonClickEmitter.emit(BmButtons.DELETE);
    }
    this.show.modalThinkTwice = false;
  }

  private showInvalidModelNotification():void {
    if (this.budgetForm.controls.yearsNumber.hasError(BmValidationErrors.MAX)) {
      this.notificationsService.warning(NOTIFICATIONS.MAX_YEARS_NUMBERS_WARNING);
    }
    if (this.budgetForm.controls.yearsNumber.hasError(BmValidationErrors.MUST_STAY_DISABLED)) {
      this.notificationsService.warning(NOTIFICATIONS.UNSAVED_YEARS_NUMBERS_WARNING);
    }
    if (this.budgetForm.controls.totalCommitment.hasError(BmValidationErrors.MUST_STAY_DISABLED)) {
      this.notificationsService.warning(NOTIFICATIONS.UNSAVED_COMMITMENT_WARNING);
    }
  }
}
